/**
 * Promise-based font loader using the browser FontFace API.
 * Tracks loaded fonts in-memory to avoid duplicate loads.
 *
 * Also injects @font-face CSS rules into a stylesheet so that
 * DOM-to-image libraries (modern-screenshot) can discover and
 * embed the fonts when rendering to PNG.
 */

const loadedFonts = new Set<string>()
const pendingLoads = new Map<string, Promise<void>>()

let fontStyleSheet: CSSStyleSheet | null = null

function getFontStyleSheet(): CSSStyleSheet {
	if (!fontStyleSheet) {
		const style = document.createElement('style')
		style.setAttribute('data-font-loader', '')
		document.head.appendChild(style)
		fontStyleSheet = style.sheet!
	}
	return fontStyleSheet
}

function detectFontFormat(url: string): string {
	const ext = url.split('?')[0].split('.').pop()?.toLowerCase()
	switch (ext) {
		case 'ttf':
			return 'truetype'
		case 'otf':
			return 'opentype'
		case 'woff':
			return 'woff'
		default:
			return 'woff2'
	}
}

/**
 * Load a single font variant via the FontFace API.
 * Returns a promise that resolves when the font is ready to render.
 *
 * @param family  - CSS font-family name (e.g. "Roboto")
 * @param fileUrl - Direct URL to the font file
 * @param weight  - CSS font-weight (default "400"), or a range like "1 900" for variable fonts
 * @param style   - CSS font-style (default "normal")
 * @param format  - CSS font format (default: auto-detected from URL)
 */
export async function loadFont(
	family: string,
	fileUrl: string,
	weight: string = '400',
	style: string = 'normal',
	format?: string,
): Promise<void> {
	const key = `${family}:${weight}:${style}`

	if (loadedFonts.has(key)) { return }

	const pending = pendingLoads.get(key)
	if (pending) { return pending }

	const promise = (async () => {
		try {
			const fontFace = new FontFace(family, `url(${fileUrl})`, { weight, style })
			const loaded = await fontFace.load()
			document.fonts.add(loaded)

			// Also inject a @font-face rule so modern-screenshot can embed the font
			const resolvedFormat = format ?? detectFontFormat(fileUrl)
			const sheet = getFontStyleSheet()
			sheet.insertRule(
				`@font-face {
					font-family: '${family}';
					src: url('${fileUrl}') format('${resolvedFormat}');
					font-weight: ${weight};
					font-style: ${style};
				}`,
				sheet.cssRules.length,
			)

			loadedFonts.add(key)
		} finally {
			pendingLoads.delete(key)
		}
	})()

	pendingLoads.set(key, promise)
	return promise
}

/**
 * Check whether a specific font variant has already been loaded.
 */
export function isFontLoaded(family: string, weight: string = '400', style: string = 'normal'): boolean {
	return loadedFonts.has(`${family}:${weight}:${style}`)
}
